import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getListOfProducts,
  searchAndFilterProducts,
  getSingleProductDetail,
  updateProductDetail,
  toggleProductStatus,
  getAllProductDefaultProperties,
  deleteProductsAdmin
} from '../../services/productService';
import { getCategoriesAndSubcategories } from '../../services/productTemplates';

// Async thunks
export const fetchProductsList = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getListOfProducts();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchAndFilter = createAsyncThunk(
  'products/searchAndFilter',
  async ({ searchTerm, category, subCategory, priceMin, priceMax, stockLevel, status }, { rejectWithValue }) => {
    try {
      const response = await searchAndFilterProducts(
        searchTerm,
        category,
        subCategory,
        priceMin,
        priceMax,
        stockLevel,
        status
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProductDetails = createAsyncThunk(
  'products/fetchProductDetails',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await getSingleProductDetail(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      const response = await updateProductDetail(id, productData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCategoriesAndSubcategories = createAsyncThunk(
  'products/fetchCategoriesAndSubcategories',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getCategoriesAndSubcategories();
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const toggleProductStat = createAsyncThunk(
  'products/toggleProductStatus',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await toggleProductStatus(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAllProductDefaultProperty = createAsyncThunk(
  'products/getAllProductDefaultProperty',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllProductDefaultProperties();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const deleteProducts = createAsyncThunk(
  'products/deleteProducts',
  async (productsId, { rejectWithValue }) => {
    try {
      const response = await deleteProductsAdmin(productsId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const productsSlice = createSlice({
  name: 'products',
  initialState: {
    data: [],
    categories: [],
    subcategories: [],
    currentProduct: null,
    loading: false,
    error: null,
    isSearching: false,

    categoriesLoading: false,
    categoriesError: null,
    productDefaultProperties: [],
  },
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProductsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsList.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchProductsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search and Filter
      .addCase(searchAndFilter.pending, (state) => {
        state.loading = true;
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchAndFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.data = action.payload;
      })
      .addCase(searchAndFilter.rejected, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.error = action.payload;
      })

      // Fetch Product Details
      .addCase(fetchProductDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        // Update the product in the list
        state.data = state.data.map(product =>
          product.id === action.payload.id ? action.payload : product
        );
        state.currentProduct = action.payload;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCategoriesAndSubcategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchCategoriesAndSubcategories.fulfilled, (state, action) => {
        // state.categoriesLoading = false;
        // state.categories = action.payload.categories || [];
        // state.subcategories = action.payload.subcategories || [];

        state.categoriesLoading = false;
        // The API returns an array of categories with subcategories nested inside
        state.categories = action.payload.map(cat => ({
          id: cat.id,
          category_name: cat.category_name,
          category_description: cat.category_description
        }));

        // Flatten all subcategories into one array
        state.subcategories = action.payload.flatMap(cat =>
          cat.sub_categories.map(sub => ({
            id: sub.id,
            sub_category_name: sub.sub_category_name,
            category: cat.id // Reference to parent category
          }))
        );

      })
      .addCase(fetchCategoriesAndSubcategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      })
      // Toggle Product Status
      .addCase(toggleProductStat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleProductStat.fulfilled, (state, action) => {
        state.loading = false;
        // Update the product status in the list
        state.data = state.data.map(product =>
          product.id === action.payload.id ? action.payload : product
        );
      })
      .addCase(toggleProductStat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get All Product Default Properties
      .addCase(getAllProductDefaultProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProductDefaultProperty.fulfilled, (state, action) => {
        state.loading = false;
        state.productDefaultProperties = action.payload;
      })
      .addCase(getAllProductDefaultProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Products
      .addCase(deleteProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProducts.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove deleted products from the list
        const deletedIds = action.payload.deleted || [];
        state.data = state.data.filter(product => !deletedIds.includes(product.id));
        state.loading = false;
      })
      .addCase(deleteProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });


  }
});

export const { clearCurrentProduct } = productsSlice.actions;
export default productsSlice.reducer;